import React from 'react';
import {Image, Platform, Pressable, StyleSheet, View} from 'react-native';
import {SvgCssUri} from 'react-native-svg';
import {Carousel} from 'react-responsive-carousel';
import {observer} from 'mobx-react';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import BrandsBannerHbTc from '../images/brand/BANNER_760x300.jpg';
import BrandsBannerBmSns from '../images/brand/BANNER_1290x510.webp';
import {AirtableAttachmentDetail, AirtableBrand} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import useAppNavigation from '../navigation/useAppNavigation';
import {useStores} from '../stores/use-stores';
import {isBmOrSnsOrDev} from '../utils/tenants';

import Text from './ui-controls/Text';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

const chunk = <T,>(arr: T[], chunkSize: number) => {
  const chunked: T[][] = [];
  const local = [...arr];
  while (local.length) {
    chunked.push(local.splice(0, chunkSize));
  }
  return chunked;
};

const BrandCarousel = observer(() => {
  const {brandsStore} = useStores();
  const navigation = useAppNavigation();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  if (!brandsStore.brands.length) {
    return null;
  }

  const numberOfBrandsPerSlide = isWebDesktopScreen ? 6 : 4;
  const brandsGroupedByNumberOfBrandsPerSlide = chunk(brandsStore.brands, numberOfBrandsPerSlide);

  const brandTileWidthStyle = StyleSheet.create({
    main: {
      width: `${100 / numberOfBrandsPerSlide - 1}%`,
    },
  });

  const getBrandImage = (detail: AirtableAttachmentDetail, key: React.Key): React.ReactNode => {
    const supportedImageExtensions = ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'];
    if (supportedImageExtensions.includes(detail.type)) {
      return (
        <View style={[styles.logoImageHolder, brandTileWidthStyle.main]} key={key}>
          <Image source={{uri: detail.url}} style={styles.logoImage} resizeMode="contain" />
        </View>
      );
    } else if (detail.type.endsWith('svg+xml')) {
      return (
        <View style={styles.logoImageSVG} key={key}>
          <SvgCssUri width="95%" height="95%" uri={detail.url} />
        </View>
      );
    } else {
      return (
        <View style={styles.logoImage}>
          <Text>unk</Text>
        </View>
      );
    }
  };

  const brandLogoCarouselCard = ({item, index}: {item: AirtableBrand[]; index: number}) => (
    <Pressable
      onPress={() => {
        navigation.navigate(AppRoute.FAVOURITE_BRANDS);
      }}
      style={[
        styles.slidingLogoRow,
        {
          justifyContent: item.length === numberOfBrandsPerSlide ? 'space-between' : 'space-evenly',
        },
      ]}
      key={index}>
      {item?.map((brand, i) => getBrandImage(brand.logo[0], `${i}-${brand.brand}`))}
    </Pressable>
  );

  const LogoCarousel = () => (
    <View style={styles.carouselComponent}>
      <Carousel
        showThumbs={false}
        autoPlay
        infiniteLoop
        interval={7500}
        showStatus={false}
        showArrows
        transitionTime={800}
        showIndicators={false}
        autoFocus>
        {brandsGroupedByNumberOfBrandsPerSlide.map((item, index) =>
          brandLogoCarouselCard({item, index}),
        )}
      </Carousel>
    </View>
  );

  const bannerImage = isBmOrSnsOrDev ? BrandsBannerBmSns : BrandsBannerHbTc;

  return (
    <View style={styles.brandBannerHolder} testID="brand-carousel">
      <Pressable onPress={() => navigation.navigate(AppRoute.FAVOURITE_BRANDS)}>
        <Image source={bannerImage} style={styles.brandBannerImage} resizeMode="contain" />
      </Pressable>
      <View style={styles.logoRow} testID="brand-carousel-logo-carousel">
        <LogoCarousel />
      </View>
    </View>
  );
});

export default BrandCarousel;

const styles = StyleSheet.create({
  brandBannerHolder: {
    width: '100%',
    ...Platform.select({
      native: {
        aspectRatio: 760 / (300 + 200),
      },
    }),
    marginBottom: 8,
    overflow: 'visible',
  },
  brandBannerImage: {
    width: undefined,
    height: undefined,
    aspectRatio: 760 / 300,
  },
  logoRow: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: 'white',
  },
  slidingLogoRow: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'visible',
    paddingHorizontal: 4,
  },
  logoImageHolder: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 2,
    borderRadius: 4,
    backgroundColor: 'white',
    aspectRatio: 1,
    padding: 8,
  },
  logoImage: {
    height: '100%',
    aspectRatio: 1,
    backgroundColor: 'white',
  },
  logoImageSVG: {
    backgroundColor: 'white',
    height: '90%',
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',

    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 2,
  },
  carouselComponent: {
    width: '100%',
  },
});
